import { useMemo } from 'react';
import type { Nullable } from 'corgi-types';
import { ArticleLabelType } from 'graphql-ts-types';

import { useLocalization } from '../../providers/LocaleProvider';
import type { ArticleLabel, ArticleLabelsList } from '../../types/labels';
import { filterNonSaleLabels, getLabelPriority, getLabelStringValue } from '../../utils/labels';

export enum OverlayLabelKind {
  Sponsored = 'sponsored',
  Label = 'label',
}

export type OverlayLabel =
  | { kind: OverlayLabelKind.Sponsored; owner: string }
  | { kind: OverlayLabelKind.Label; label: ArticleLabel };

export interface UseOverlayLabelsOptions {
  labels_v2: ArticleLabelsList;
  sponsored?: Nullable<{ owner?: Nullable<string> }>;
  hideHome24Tipp?: boolean;
  maxItems?: number;
  sortLongestBelow?: boolean;
}

const getOverlayPriority = (item: OverlayLabel): number =>
  item.kind === OverlayLabelKind.Sponsored ? -1 : getLabelPriority(item.label.type);

/**
 * Builds the list of overlay labels to display above the product image.
 * 1. Collects sponsored + non-sale labels
 * 2. Sorts by priority and caps at maxItems
 * 3. Optionally reorders longest label last (for PLP row layout)
 */
export const useOverlayLabels = ({
  labels_v2,
  sponsored,
  hideHome24Tipp,
  maxItems = 2,
  sortLongestBelow = false,
}: UseOverlayLabelsOptions): OverlayLabel[] => {
  const { t, fc } = useLocalization();

  return useMemo(() => {
    const all: OverlayLabel[] = [];

    if (sponsored?.owner) {
      all.push({ kind: OverlayLabelKind.Sponsored, owner: sponsored.owner });
    }

    const nonSale = filterNonSaleLabels(labels_v2);
    nonSale.forEach(label => {
      if (label) {
        if (hideHome24Tipp && label.type === ArticleLabelType.Home24Selection) return;
        all.push({ kind: OverlayLabelKind.Label, label });
      }
    });

    const sorted = [...all]
      .sort((a, b) => getOverlayPriority(a) - getOverlayPriority(b))
      .slice(0, maxItems);

    if (!sortLongestBelow || sorted.length <= 1) return sorted;

    const getText = (item: OverlayLabel) =>
      item.kind === OverlayLabelKind.Sponsored
        ? t('h24_product_card_sponsored')
        : getLabelStringValue(item.label.value, item.label.type, fc);

    return [...sorted].sort((a, b) => getText(a).length - getText(b).length);
  }, [labels_v2, sponsored?.owner, hideHome24Tipp, maxItems, sortLongestBelow, t, fc]);
};

export const useAriaLabel = (type: ArticleLabel['type'], isClickable: boolean, value?: string) => {
  const { t, r } = useLocalization();
  let label: string;

  switch (type) {
    case ArticleLabelType.New:
      label = t('h24_product_label_new');
      break;
    case ArticleLabelType.PercentageDiscount:
      const numericValue = parseFloat(value?.replace(/[^0-9.]/g, '') ?? '');
      label = r(t('h24_product_label_discount'), { value: numericValue });
      break;
    case ArticleLabelType.Campaign:
    case ArticleLabelType.ValuePack:
      label = r(t('h24_product_label_campaign'), { name: value });
      break;
    case ArticleLabelType.Home24Selection:
      label = t('h24_product_label_home24_selection');
      break;
    default:
      label = '';
  }

  return isClickable ? `${label}. ${t('h24_learn_more')}.` : label;
};
